/**
 * EmailContent - Safe HTML Email Renderer
 *
 * Renders email HTML content safely using:
 * - Quoted-printable decoding (for encoded emails)
 * - DOMPurify sanitization (XSS prevention)
 * - Sandboxed iframe isolation (CSS/JS isolation)
 * - External image blocking with user consent
 *
 * Best practices for email HTML display:
 * 1. Never trust email HTML - always sanitize
 * 2. Isolate in iframe to prevent CSS leakage
 * 3. Block external images by default (tracking prevention)
 * 4. Handle cid: embedded images
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';

interface EmailContentProps {
  html?: string | null;
  text?: string | null;
  className?: string;
  /** Allow external images (default: false for privacy) */
  allowExternalImages?: boolean;
  /** Callback when user requests to load images */
  onLoadImages?: () => void;
}

/**
 * Decode quoted-printable encoded content
 * Handles =XX hex codes and soft line breaks (=\r\n or =\n)
 */
function decodeQuotedPrintable(input: string): string {
  if (!input) return '';

  return input
    // Remove soft line breaks (= followed by newline)
    .replace(/=\r?\n/g, '')
    // Decode =XX hex sequences
    .replace(/=([0-9A-Fa-f]{2})/g, (_, hex) => {
      return String.fromCharCode(parseInt(hex, 16));
    });
}

/**
 * Check if content appears to be quoted-printable encoded
 */
function isQuotedPrintable(content: string): boolean {
  if (!content) return false;
  // Look for common QP patterns: =3D (equals), =20 (space), soft breaks
  return /=3D|=20|=\r?\n/.test(content);
}

/**
 * Check if content is MIME multipart
 */
function isMimeMultipart(content: string): boolean {
  if (!content) return false;
  // Look for MIME boundary markers
  return /^--[-=_a-zA-Z0-9]+\r?\n/m.test(content) ||
         /----==_mimepart_/.test(content) ||
         /^------=_/.test(content);
}

/**
 * Parse MIME multipart content and extract the best part (HTML preferred, then text)
 */
function parseMimeMultipart(content: string): { html: string | null; text: string | null } {
  if (!content) return { html: null, text: null };

  // Find the boundary - look for pattern like ----==_mimepart_XXX or ------=_XXX
  const boundaryMatch = content.match(/^(--[-=_a-zA-Z0-9]+)\r?\n/m) ||
                        content.match(/(----==_mimepart_[a-zA-Z0-9_]+)/);

  if (!boundaryMatch) {
    return { html: null, text: null };
  }

  const boundary = boundaryMatch[1];
  const parts = content.split(boundary);

  let htmlPart: string | null = null;
  let textPart: string | null = null;

  for (const part of parts) {
    if (!part || part === '--' || part.trim() === '--') continue;

    // Parse headers and body
    const headerBodySplit = part.match(/^([\s\S]*?)\r?\n\r?\n([\s\S]*)$/);
    if (!headerBodySplit) continue;

    const headers = headerBodySplit[1].toLowerCase();
    let body = headerBodySplit[2];

    // Check content type
    const isHtml = headers.includes('text/html');
    const isText = headers.includes('text/plain');
    const isQuotedPrintableEncoded = headers.includes('quoted-printable');

    // Decode if quoted-printable
    if (isQuotedPrintableEncoded) {
      body = decodeQuotedPrintable(body);
    }

    // Remove trailing boundary marker
    body = body.replace(/\r?\n--$/, '').trim();

    if (isHtml && !htmlPart) {
      htmlPart = body;
    } else if (isText && !textPart) {
      textPart = body;
    }
  }

  return { html: htmlPart, text: textPart };
}

/**
 * Check if content is HTML
 */
function isHtmlContent(content: string): boolean {
  if (!content) return false;
  const trimmed = content.trim().toLowerCase();
  return trimmed.startsWith('<!doctype') ||
         trimmed.startsWith('<html') ||
         trimmed.startsWith('<body') ||
         /<(div|table|p|span|a|img|br|hr)\b/i.test(content);
}

/**
 * Sanitize HTML for safe display
 */
function sanitizeHtml(html: string, allowImages: boolean): string {
  // Configure DOMPurify with allowed tags and attributes
  const sanitized = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      // Structure
      'html', 'head', 'body', 'div', 'span', 'section', 'article', 'header', 'footer',
      // Text
      'p', 'br', 'hr', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'del', 'ins',
      'sub', 'sup', 'small', 'big', 'mark', 'abbr', 'cite', 'code', 'pre',
      // Lists
      'ul', 'ol', 'li', 'dl', 'dt', 'dd',
      // Tables
      'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
      // Links and media
      'a', 'img', 'figure', 'figcaption',
      // Quotes
      'blockquote', 'q',
      // Other
      'address', 'center', 'font',
    ],
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'title', 'width', 'height',
      'style', 'class', 'id', 'name',
      'colspan', 'rowspan', 'align', 'valign', 'border', 'cellpadding', 'cellspacing',
      'bgcolor', 'color', 'face', 'size',
      'target', 'rel',
      'dir', 'lang',
    ],
    ALLOW_DATA_ATTR: false,
    ADD_ATTR: ['target'],
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'select', 'textarea'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
  }) as string;

  let result = sanitized;

  // If external images not allowed, replace src with placeholder
  if (!allowImages) {
    result = result.replace(
      /<img([^>]*)\ssrc=["']?(https?:\/\/[^"'\s>]+)["']?([^>]*)>/gi,
      '<img$1 data-blocked-src="$2" src="data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'100\' height=\'100\'%3E%3Crect fill=\'%23f0f0f0\' width=\'100\' height=\'100\'/%3E%3Ctext x=\'50\' y=\'50\' text-anchor=\'middle\' dy=\'.3em\' fill=\'%23999\' font-size=\'10\'%3EImage blocked%3C/text%3E%3C/svg%3E"$3>'
    );
  }

  // Make all links open in new tab
  result = result.replace(
    /<a\s/gi,
    '<a target="_blank" rel="noopener noreferrer" '
  );

  return result;
}

/**
 * Create a complete HTML document for the iframe
 */
function createEmailDocument(content: string, isDarkMode: boolean): string {
  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <base target="_blank">
  <style>
    /* Reset and base styles */
    * {
      box-sizing: border-box;
    }
    html, body {
      margin: 0;
      padding: 0;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      font-size: 14px;
      line-height: 1.5;
      color: ${isDarkMode ? '#e5e7eb' : '#374151'};
      background: ${isDarkMode ? '#1f2937' : '#ffffff'};
      word-wrap: break-word;
      overflow-wrap: break-word;
    }
    body {
      padding: 16px;
    }
    /* Links */
    a {
      color: #2563eb;
      text-decoration: none;
    }
    a:hover {
      text-decoration: underline;
    }
    /* Images */
    img {
      max-width: 100%;
      height: auto;
    }
    /* Tables - common in emails */
    table {
      border-collapse: collapse;
      max-width: 100%;
    }
    td, th {
      padding: 4px 8px;
    }
    /* Blockquotes - email replies */
    blockquote {
      margin: 8px 0;
      padding-left: 12px;
      border-left: 3px solid #d1d5db;
      color: #6b7280;
    }
    /* Pre/code */
    pre, code {
      font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      font-size: 13px;
      background: ${isDarkMode ? '#374151' : '#f3f4f6'};
      border-radius: 4px;
    }
    pre {
      padding: 12px;
      overflow-x: auto;
    }
    code {
      padding: 2px 4px;
    }
    /* Blocked image placeholder */
    img[data-blocked-src] {
      border: 1px dashed #d1d5db;
      border-radius: 4px;
      min-width: 60px;
      min-height: 40px;
    }
  </style>
</head>
<body>
  ${content}
</body>
</html>
`;
}

const EmailContent: React.FC<EmailContentProps> = ({
  html,
  text,
  className = '',
  allowExternalImages = false,
  onLoadImages,
}) => {
  const { t } = useTranslation('email');
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState(200);
  const [hasBlockedImages, setHasBlockedImages] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(allowExternalImages);

  // Determine content and process it
  const processedContent = useMemo(() => {
    // Start with provided content
    let content = html || text || '';

    // Check if content is MIME multipart (raw email body not properly parsed)
    if (isMimeMultipart(content)) {
      const parsed = parseMimeMultipart(content);
      // Prefer HTML part, fall back to text part
      if (parsed.html) {
        content = parsed.html;
      } else if (parsed.text) {
        content = parsed.text;
      }
      // Content is already decoded by parseMimeMultipart if it was quoted-printable
    } else {
      // Decode quoted-printable if needed (for non-multipart content)
      if (isQuotedPrintable(content)) {
        content = decodeQuotedPrintable(content);
      }
    }

    // If it's plain text, convert to simple HTML
    if (!isHtmlContent(content)) {
      content = `<pre style="white-space: pre-wrap; font-family: inherit;">${
        content
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/\n/g, '<br>')
      }</pre>`;
    }

    // Sanitize
    const sanitized = sanitizeHtml(content, imagesLoaded);

    // Check if there are blocked images
    const hasBlocked = /data-blocked-src=/.test(sanitized);
    setHasBlockedImages(hasBlocked && !imagesLoaded);

    return sanitized;
  }, [html, text, imagesLoaded]);

  // Create the full document
  const iframeDoc = useMemo(() => {
    // Check for dark mode (could be enhanced with context)
    const isDarkMode = document.documentElement.classList.contains('dark');
    return createEmailDocument(processedContent, isDarkMode);
  }, [processedContent]);

  // Update iframe content and height
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;

    // Write content to iframe
    doc.open();
    doc.write(iframeDoc);
    doc.close();

    // Adjust height to content
    const updateHeight = () => {
      const body = doc.body;
      if (body) {
        const height = Math.max(body.scrollHeight, body.offsetHeight);
        setIframeHeight(Math.min(height + 32, 2000)); // Cap at 2000px
      }
    };

    // Update height after content loads
    updateHeight();

    // Also update after images load
    const images = doc.getElementsByTagName('img');
    Array.from(images).forEach(img => {
      img.addEventListener('load', updateHeight);
      img.addEventListener('error', updateHeight);
    });

    // Observe for dynamic content
    const observer = new ResizeObserver(updateHeight);
    if (doc.body) {
      observer.observe(doc.body);
    }

    return () => {
      observer.disconnect();
    };
  }, [iframeDoc]);

  const handleLoadImages = () => {
    setImagesLoaded(true);
    onLoadImages?.();
  };

  return (
    <div className={`email-content ${className}`}>
      {/* Blocked images banner */}
      {hasBlockedImages && (
        <div className="mb-3 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center justify-between text-sm">
          <div className="flex items-center gap-2 text-yellow-800">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span>{t('thread.images_hidden')}</span>
          </div>
          <button
            onClick={handleLoadImages}
            className="px-3 py-1 bg-yellow-100 hover:bg-yellow-200 text-yellow-800 rounded transition text-xs font-medium"
          >
            {t('thread.load_images')}
          </button>
        </div>
      )}

      {/* Sandboxed iframe for email content */}
      <iframe
        ref={iframeRef}
        title={t('common.email_content')}
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        style={{
          width: '100%',
          height: `${iframeHeight}px`,
          border: 'none',
          display: 'block',
        }}
        className="bg-white rounded"
      />
    </div>
  );
};

export default EmailContent;
